import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

/**
 * Calls `onItemVisible` with the position of each item the first time it is
 * scrolled into view, then stops watching that item.
 *
 * One observer for the whole track rather than one per card. A carousel of forty
 * products is forty elements, and forty observers is forty times the work for an
 * answer one observer already has.
 *
 * The position comes from the item's place among the track's children rather
 * than from a captured index, so nothing has to be threaded through the render.
 */
export const useItemImpressions = (
  trackRef: RefObject<HTMLElement>,
  onItemVisible: ((index: number) => void) | undefined,
  itemCount: number,
) => {
  /* Held in a ref so an inline handler, which is the normal way to pass one,
     does not tear down and re-create the observer on every render. */
  const callback = useRef(onItemVisible);
  callback.current = onItemVisible;

  useEffect(() => {
    const track = trackRef.current;

    if (!track || !itemCount || !callback.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          /* Unobserving is what makes the impression fire once: an item scrolled
             past and back to has not been seen twice. */
          observer.unobserve(entry.target);

          const index = Array.prototype.indexOf.call(track.children, entry.target);

          if (index !== -1) {
            callback.current?.(index);
          }
        });
      },
      { root: track },
    );

    Array.from(track.children).forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [itemCount, trackRef]);
};
